import React from "react";

/* The copilot's face.
 *
 * Redrawn as vector rather than dropped in as the source image: at 28px a raster
 * mark is the one soft, slightly wrong thing on a page of crisp type, it doubles
 * in weight on a retina screen, and it can't take the colour of whatever it sits
 * on. As paths it stays sharp at any size and inherits currentColor, so the same
 * glyph works white-on-gradient here and magenta-on-white anywhere else.
 *
 * Kept in its own file because an avatar renders once per turn — a shape defined
 * inline would have to be hand-synced every time it's reused.
 */

/* Everything sits 1.2 units below the geometric centre of the viewBox. The
   antenna is all weight and no mass above the head, so a mathematically centred
   mark reads as sitting high — this puts the head where the eye expects it. */
export const CopilotGlyph = ({ size = 16, strokeWidth = 2, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    {/* Antenna. Drawn before the head so the stem tucks under the top edge
        rather than crossing it. */}
    <path d="M12 4.2v2.7" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
    <circle cx="12" cy="3.2" r="1.2" fill="currentColor" />
    {/* Head — a squircle, not a circle: the flatter top is what reads as a face
        rather than a generic dot. */}
    <rect
      x="3.9"
      y="6.8"
      width="16.2"
      height="13.6"
      rx="5.2"
      stroke="currentColor"
      strokeWidth={strokeWidth}
    />
    <circle cx="9" cy="13.6" r="1.8" fill="currentColor" />
    <circle cx="15" cy="13.6" r="1.8" fill="currentColor" />
  </svg>
);

/* The avatar chip.
 *
 * The premium here is not the glyph — it's the object it sits in: a gradient
 * that runs across the mark instead of behind it, a hairline inner ring so the
 * edge stays crisp against white, a top-down sheen because every physical thing
 * is lit from above, and a coloured shadow rather than a grey one so it looks
 * lit rather than cut out.
 */
export const CopilotAvatar = () => (
  <span className="relative flex h-7 w-7 flex-none items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#B85BAE] via-[#A04A97] to-[#C86B4F] text-white shadow-[0_2px_6px_-2px_rgba(160,74,151,0.7)] ring-1 ring-inset ring-white/25">
    <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent" />
    <CopilotGlyph size={16} className="relative" />
  </span>
);

export default CopilotAvatar;
